import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@habits/shared/solid";
import {
  calculateCompletionPercentage,
  cn,
  getAllCompletionsQuery,
  getHabitsQuery,
  queryKeys,
} from "@habits/shared";
import { BarChart3 } from "@fleet/icons";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@fleet/utils";

interface StatsSectionProps {
  userId: string;
}

export function StatsSection(props: StatsSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const habits = createPowerSyncQuery({
    queryKey: () => queryKeys.habits.list(props.userId, true),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getHabitsQuery(dbInstance, props.userId, { includeArchived: true })
        : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const completions = createPowerSyncQuery({
    queryKey: () => queryKeys.habits.completions(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getAllCompletionsQuery(dbInstance, props.userId) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const habitStats = () => {
    const rows = habits.data ?? [];
    return {
      total: rows.length,
      active: rows.filter((h) => h.archived !== 1).length,
      archived: rows.filter((h) => h.archived === 1).length,
    };
  };
  const doneCount = () => (completions.data ?? []).filter((c) => c.status === "done").length;
  const frozenCount = () => (completions.data ?? []).filter((c) => c.status === "skipped").length;
  const completionPct = () =>
    calculateCompletionPercentage(doneCount(), doneCount() + frozenCount());

  return (
    <Card>
      <CardHeader>
        <div class="flex items-center gap-2">
          <BarChart3 class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Statistics`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label={t(msg`Habits`)}
            value={habitStats().total}
            colorClass="text-primary"
            bgClass="bg-primary/10"
            isLoading={habits.isPending}
          />
          <StatCard
            label={t(msg`Completed`)}
            value={doneCount()}
            colorClass="text-success"
            bgClass="bg-success/10"
            isLoading={completions.isPending}
          />
          <StatCard
            label={t(msg`Freezes`)}
            value={frozenCount()}
            colorClass="text-warning"
            bgClass="bg-warning/10"
            isLoading={completions.isPending}
          />
          <StatCard
            label={t(msg`Archived`)}
            value={habitStats().archived}
            colorClass="text-foreground"
            bgClass="bg-muted/50"
            isLoading={habits.isPending}
          />
        </div>

        <Show when={doneCount() + frozenCount() > 0 && !completions.isPending}>
          <div class="space-y-2">
            <div class="flex items-center justify-between text-xs text-muted-foreground">
              <span>{t(msg`Done vs frozen`)}</span>
              <span class="font-bold text-foreground">{completionPct()}%</span>
            </div>
            <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                class="h-full bg-green-500 transition-transform duration-500 origin-left"
                style={{ transform: `scaleX(${completionPct() / 100})` }}
              />
            </div>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}

function StatCard(props: {
  label: string;
  value: number;
  colorClass: string;
  bgClass: string;
  isLoading?: boolean;
}) {
  const { locale } = useI18n();
  const isCompact = () => props.value >= COMPACT_THRESHOLD;
  const formattedValue = () => formatCompactNumber(props.value, locale());
  const fullLabel = () => `${props.value.toLocaleString(locale())} ${props.label.toLowerCase()}`;

  return (
    <div
      class={cn(
        "flex flex-col items-center justify-center rounded-2xl p-4 text-center transition-all hover:scale-[1.02]",
        props.bgClass,
      )}
    >
      <Show
        when={props.isLoading}
        fallback={
          <Show
            when={isCompact()}
            fallback={
              <span
                class={cn(
                  "text-2xl font-black leading-none tracking-tight select-none",
                  props.colorClass,
                )}
                aria-label={fullLabel()}
              >
                {props.value}
              </span>
            }
          >
            <Tooltip openDelay={350} closeDelay={100}>
              <TooltipTrigger class="focus-visible:outline-none select-none cursor-help">
                <span
                  class={cn(
                    "text-2xl font-black leading-none tracking-tight border-b border-dashed border-current pb-0.5",
                    props.colorClass,
                  )}
                  aria-label={fullLabel()}
                >
                  {formattedValue()}
                </span>
              </TooltipTrigger>
              <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
                {fullLabel()}
              </TooltipContent>
            </Tooltip>
          </Show>
        }
      >
        <Skeleton class="h-7 w-8" />
      </Show>
      <div class="mt-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-80 select-none">
        {props.label}
      </div>
    </div>
  );
}
